import { useState } from 'react'
import { aggregateApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import { BarChart3 } from 'lucide-react'
import { formatCurrency, formatNumber } from '@/utils/currency'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

interface PLClientRow {
  client_id: string
  client_name: string
  currency: string
  total_revenue: string
  total_expenses: string
  net_profit: string
}

interface PLReport {
  start_date: string
  end_date: string
  clients: PLClientRow[]
  firm_total_revenue: string
  firm_total_expenses: string
  firm_net_profit: string
}

interface BSClientRow {
  client_id: string
  client_name: string
  currency: string
  total_assets: string
  total_liabilities: string
  total_equity: string
}

interface BSReport {
  as_at: string
  clients: BSClientRow[]
  firm_total_assets: string
  firm_total_liabilities: string
  firm_total_equity: string
}

type Tab = 'pl' | 'bs'

export default function FirmReports() {
  const { currentFirm } = useAuth()
  const [tab, setTab] = useState<Tab>('pl')
  const [loading, setLoading] = useState(false)

  // P&L state
  const [plStartDate, setPlStartDate] = useState(() => `${new Date().getFullYear()}-01-01`)
  const [plEndDate, setPlEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [plReport, setPlReport] = useState<PLReport | null>(null)

  // BS state
  const [bsAsAt, setBsAsAt] = useState(() => new Date().toISOString().split('T')[0])
  const [bsReport, setBsReport] = useState<BSReport | null>(null)

  async function generatePL() {
    if (!currentFirm) return
    setLoading(true)
    try {
      const res = await aggregateApi.profitLoss(currentFirm.id, plStartDate, plEndDate)
      setPlReport(res.data)
    } catch {
      toast.error('Failed to generate report')
    } finally { setLoading(false) }
  }

  async function generateBS() {
    if (!currentFirm) return
    setLoading(true)
    try {
      const res = await aggregateApi.balanceSheet(currentFirm.id, bsAsAt)
      setBsReport(res.data)
    } catch {
      toast.error('Failed to generate report')
    } finally { setLoading(false) }
  }

  const netColor = (val: string) =>
    parseFloat(val) >= 0 ? 'text-emerald-700' : 'text-red-700'

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Firm Reports</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Aggregate financials across all active clients</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b-2 border-[#CCCED2]">
        {(['pl', 'bs'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] transition-colors',
              tab === t
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent'
            )}
          >
            {t === 'pl' ? 'Profit & Loss' : 'Balance Sheet'}
          </button>
        ))}
      </div>

      {tab === 'pl' && (
        <div>
          <div className="flex gap-3 mb-5 items-end">
            <div>
              <label className="input-label">From</label>
              <input
                type="date"
                value={plStartDate}
                onChange={(e) => setPlStartDate(e.target.value)}
                className="input py-1.5"
              />
            </div>
            <div>
              <label className="input-label">To</label>
              <input
                type="date"
                value={plEndDate}
                onChange={(e) => setPlEndDate(e.target.value)}
                className="input py-1.5"
              />
            </div>
            <button onClick={generatePL} disabled={loading} className="btn-primary py-[7px]">
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>

          {plReport ? (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{plReport.start_date} to {plReport.end_date}</p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <th className="table-header">Client</th>
                    <th className="table-header text-right">Revenue</th>
                    <th className="table-header text-right">Expenses</th>
                    <th className="table-header text-right">Net Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {plReport.clients.map((c) => (
                    <tr key={c.client_id} className="border-b border-surface-50 hover:bg-surface-50">
                      <td className="table-cell font-bold text-[#000A1E]">{c.client_name}</td>
                      <td className="table-cell text-right tabular-nums text-[#000A1E]">
                        {formatCurrency(c.total_revenue, c.currency)}
                      </td>
                      <td className="table-cell text-right tabular-nums text-[#000A1E]">
                        {formatCurrency(c.total_expenses, c.currency)}
                      </td>
                      <td className={`table-cell text-right tabular-nums font-bold ${netColor(c.net_profit)}`}>
                        {formatCurrency(c.net_profit, c.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-surface-100 border-t-2 border-surface-200">
                    <td className="table-cell font-bold text-[#000A1E]">
                      Firm Total
                      <span className="ml-1.5 text-[11px] font-normal text-[rgba(0,10,30,0.75)]">(mixed currencies)</span>
                    </td>
                    <td className="table-cell text-right tabular-nums font-bold text-[#000A1E]">
                      {formatNumber(plReport.firm_total_revenue)}
                    </td>
                    <td className="table-cell text-right tabular-nums font-bold text-[#000A1E]">
                      {formatNumber(plReport.firm_total_expenses)}
                    </td>
                    <td className={`table-cell text-right tabular-nums font-bold ${netColor(plReport.firm_net_profit)}`}>
                      {formatNumber(plReport.firm_net_profit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {plReport.clients.length === 0 && (
                <div className="p-8 text-center text-[13px] text-[rgba(0,10,30,0.75)]">No active clients found</div>
              )}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <BarChart3 className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date range and click Generate</p>
            </div>
          )}
        </div>
      )}

      {tab === 'bs' && (
        <div>
          <div className="flex gap-3 mb-5 items-end">
            <div>
              <label className="input-label">As At</label>
              <input
                type="date"
                value={bsAsAt}
                onChange={(e) => setBsAsAt(e.target.value)}
                className="input py-1.5"
              />
            </div>
            <button onClick={generateBS} disabled={loading} className="btn-primary py-[7px]">
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>

          {bsReport ? (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                <p className="text-[13px] text-[rgba(0,10,30,0.75)]">As at {bsReport.as_at}</p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <th className="table-header">Client</th>
                    <th className="table-header text-right">Assets</th>
                    <th className="table-header text-right">Liabilities</th>
                    <th className="table-header text-right">Equity</th>
                  </tr>
                </thead>
                <tbody>
                  {bsReport.clients.map((c) => (
                    <tr key={c.client_id} className="border-b border-surface-50 hover:bg-surface-50">
                      <td className="table-cell font-bold text-[#000A1E]">{c.client_name}</td>
                      <td className="table-cell text-right tabular-nums text-[#000A1E]">
                        {formatCurrency(c.total_assets, c.currency)}
                      </td>
                      <td className="table-cell text-right tabular-nums text-[#000A1E]">
                        {formatCurrency(c.total_liabilities, c.currency)}
                      </td>
                      <td className="table-cell text-right tabular-nums text-[#000A1E]">
                        {formatCurrency(c.total_equity, c.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-surface-100 border-t-2 border-surface-200">
                    <td className="table-cell font-bold text-[#000A1E]">
                      Firm Total
                      <span className="ml-1.5 text-[11px] font-normal text-[rgba(0,10,30,0.75)]">(mixed currencies)</span>
                    </td>
                    <td className="table-cell text-right tabular-nums font-bold text-[#000A1E]">
                      {formatNumber(bsReport.firm_total_assets)}
                    </td>
                    <td className="table-cell text-right tabular-nums font-bold text-[#000A1E]">
                      {formatNumber(bsReport.firm_total_liabilities)}
                    </td>
                    <td className="table-cell text-right tabular-nums font-bold text-[#000A1E]">
                      {formatNumber(bsReport.firm_total_equity)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {bsReport.clients.length === 0 && (
                <div className="p-8 text-center text-[13px] text-[rgba(0,10,30,0.75)]">No active clients found</div>
              )}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <BarChart3 className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date and click Generate</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
